import { formatTime } from '@reach/web-ui/utils';
import type { AutomationTemplateContents } from '@reach/shared-types/automation-template';
import { BusinessAutomationActionTypeEnum } from '@reach/shared-types/business-automation';
import { Calendar } from 'lucide-react';
import React, { useContext, useState } from 'react';
import { AutomationEditorContext } from '../AutomationEditorContext';
import { Step } from '../Step';
import { WaitUntilDialog } from './WaitUntilDialog';

type WaitUntilAction = Extract<
  AutomationTemplateContents['actionData'][0],
  | {
      type: typeof BusinessAutomationActionTypeEnum.WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME;
    }
  | { type: typeof BusinessAutomationActionTypeEnum.WAIT_UNTIL_NEXT_OCCURRENCE }
>;

interface WaitUntilStepProps {
  index: number;
}

const waitUntilTypeLabels: Record<
  | typeof BusinessAutomationActionTypeEnum.WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME
  | typeof BusinessAutomationActionTypeEnum.WAIT_UNTIL_NEXT_OCCURRENCE,
  string
> = {
  [BusinessAutomationActionTypeEnum.WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME]:
    'Wait Until Next Available Time',
  [BusinessAutomationActionTypeEnum.WAIT_UNTIL_NEXT_OCCURRENCE]: 'Wait Until Next Occurrence',
};

export const WaitUntilStep: React.FC<WaitUntilStepProps> = ({ index }) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { state } = editor;
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const action = state.localContents.actionData?.[index];
  if (
    !action ||
    (action.type !== BusinessAutomationActionTypeEnum.WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME &&
      action.type !== BusinessAutomationActionTypeEnum.WAIT_UNTIL_NEXT_OCCURRENCE)
  ) {
    return null;
  }

  // TypeScript type narrowing - at this point we know action is a WaitUntilAction
  const waitUntilAction = action as WaitUntilAction;
  const { type, hour, minute } = waitUntilAction;
  const timeStr = formatTime({ hour, minute });
  const needsSetup = !timeStr;

  const subtitle = needsSetup
    ? 'Click Configure to set wait time'
    : `${waitUntilTypeLabels[type]} - ${timeStr}`;

  return (
    <>
      <Step
        index={index}
        icon={Calendar}
        title="Wait Until"
        subtitle={subtitle}
        needsSetup={needsSetup}
        onConfigure={() => setIsDialogOpen(true)}
      />
      {isDialogOpen && (
        <WaitUntilDialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          action={waitUntilAction}
          actionIndex={index}
        />
      )}
    </>
  );
};
